import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CheckCircle2, Clock, Loader2, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { TopBar } from "../components/TopBar";
import { StripePayButton } from "../components/StripePayButton";
import { DINNER_SLOT_LABELS } from "../lib/i18n";
import { formatISODate, formatCountdown } from "../lib/utils";
import { useCountdown } from "../hooks/useCountdown";

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 12; // ~30s total wait

export default function PayPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, stripe } = useApp();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pollState, setPollState] = useState("idle"); // idle | polling | paid | expired | error
  const sessionId = searchParams.get("session_id");
  const isCancel = window.location.pathname.endsWith("/cancel");
  const pollTimer = useRef(null);
  const pollAttempts = useRef(0);
  const [clockOffset, setClockOffset] = useState(0);

  const loadInfo = useCallback(() => {
    api
      .get(`/payments/${bookingId}`)
      .then(({ data }) => {
        setInfo(data);
        if (data.server_now) setClockOffset(Date.parse(data.server_now) - Date.now());
      })
      .catch((e) => {
        setError(e?.response?.status === 404 ? "not_found" : "load_failed");
      })
      .finally(() => setLoading(false));
  }, [bookingId]);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  const awaitingPayment = info?.status === "confirmed" && info?.payment_status === "pending";
  const msLeft = useCountdown(awaitingPayment ? info?.payment_deadline : null, clockOffset);
  const timeIsUp = msLeft === 0;
  // Once the window closes, re-read the booking so the server's verdict shows.
  useEffect(() => {
    if (!timeIsUp) return undefined;
    const id = setTimeout(loadInfo, 5000);
    return () => clearTimeout(id);
  }, [timeIsUp, loadInfo]);

  // If returning from Stripe with a session_id, poll for status
  const pollOnce = useCallback(async () => {
    try {
      const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPollState("paid");
        loadInfo();
        return true;
      }
      if (data.status === "expired" || data.payment_status === "expired") {
        setPollState("expired");
        return true;
      }
      // transient_error from backend → keep polling silently
      return false;
    } catch (e) {
      // Network or 5xx — keep polling, only surface error after max retries
      return false;
    }
  }, [sessionId, loadInfo]);

  useEffect(() => {
    if (!sessionId) return;
    setPollState("polling");
    pollAttempts.current = 0;
    const tick = async () => {
      pollAttempts.current += 1;
      const done = await pollOnce();
      if (done) return;
      if (pollAttempts.current >= MAX_POLL_ATTEMPTS) {
        // After max attempts, refresh booking info (auto-sweep on /bookings may
        // have finalized it server-side) and trust that as the final state.
        await loadInfo();
        setPollState("idle");
        return;
      }
      pollTimer.current = setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
    // loadInfo is already a dependency of pollOnce; listing it keeps the
    // exhaustive-deps lint happy without changing when this effect re-runs.
  }, [sessionId, pollOnce, loadInfo]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <TopBar showAdminButton={false} />
        <div className="mx-auto mt-20 max-w-md text-center text-muted-foreground">…</div>
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-screen bg-background">
        <TopBar showAdminButton={false} />
        <div className="mx-auto mt-20 max-w-md px-4 text-center">
          <p className="font-display text-lg">{error === "not_found" ? "404" : "Error"}</p>
          <Button variant="ghost" className="mt-3" onClick={() => navigate("/")}>{t("pay_back_home")}</Button>
        </div>
      </div>
    );
  }

  const isPaid = info.payment_status === "paid" || pollState === "paid";
  const isCancelled = !isPaid && (info.status === "cancelled" || timeIsUp);
  const timeLabel = info.service === "dinner"
    ? (DINNER_SLOT_LABELS[info.time] || info.time)
    : info.time;

  return (
    <div className="min-h-screen bg-background">
      <TopBar showAdminButton={false} />
      <main className="mx-auto max-w-xl px-4 py-10 sm:px-6" data-testid="pay-page">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary/15">
            {isPaid ? (
              <CheckCircle2 className="h-9 w-9 text-[hsl(var(--success))]" />
            ) : pollState === "polling" ? (
              <Loader2 className="h-9 w-9 animate-spin text-primary" />
            ) : isCancelled || pollState === "expired" || pollState === "error" ? (
              <AlertTriangle className="h-9 w-9 text-destructive" />
            ) : (
              <Clock className="h-9 w-9 text-primary" />
            )}
          </div>
          <h1 className="font-display text-2xl font-semibold" data-testid="pay-page-title">
            {isPaid
              ? t("pay_success_title")
              : isCancelled
                ? t("pay_cancelled_title")
              : pollState === "polling"
                ? t("pay_polling_title")
                : pollState === "expired"
                  ? t("pay_expired_title")
                  : pollState === "error"
                    ? t("pay_error_title")
                    : isCancel
                      ? t("pay_cancel_title")
                      : t("pay_page_title")}
          </h1>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            {isPaid ? t("pay_success_subtitle") : isCancelled ? t("pay_cancelled_subtitle") : t("pay_page_subtitle")}
          </p>
          {awaitingPayment && msLeft != null && !isCancelled && pollState !== "polling" && (
            <div className="mx-auto mt-6 w-fit rounded-2xl border border-primary/50 bg-primary/10 px-6 py-3" data-testid="pay-countdown">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t("pay_countdown_label")}</p>
              <p className={`font-display text-3xl font-semibold tabular-nums ${msLeft < 120000 ? "text-destructive" : "text-primary"}`} data-testid="pay-countdown-value">
                {formatCountdown(msLeft)}
              </p>
              <p className="mt-1 max-w-xs text-xs text-muted-foreground">{t("pay_countdown_hint")}</p>
            </div>
          )}
        </div>

        <Card className="mt-8 border-border bg-card text-left">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-display text-lg font-semibold">
                  {info.nome} {info.cognome}
                </p>
                <Badge variant="outline" className="mt-1 border-primary/50 text-primary">
                  {t(`service_${info.service}`)}
                </Badge>
              </div>
              {isPaid && (
                <Badge className="border-[hsl(var(--success)/0.5)] bg-[hsl(var(--success)/0.18)] text-[hsl(var(--success))]" data-testid="pay-status-paid">
                  PAID
                </Badge>
              )}
            </div>

            <dl className="mt-5 grid grid-cols-2 gap-4 text-sm">
              <div><dt className="text-muted-foreground">{t("summary_date")}</dt><dd className="font-medium">{formatISODate(info.date)}</dd></div>
              <div><dt className="text-muted-foreground">{t("summary_guests")}</dt><dd className="font-medium">{info.guests}</dd></div>
              {timeLabel && (
                <div><dt className="text-muted-foreground">{t("summary_time")}</dt><dd className="font-medium">{timeLabel}</dd></div>
              )}
              {!isCancelled && <div>
                <dt className="text-muted-foreground">{t("pay_amount_label")}</dt>
                <dd className="font-display text-xl font-semibold text-primary">€{info.amount}</dd>
              </div>}
            </dl>

            {!isPaid && !isCancelled && info.amount > 0 && pollState !== "polling" && (
              <div className="mt-6">
                {stripe?.enabled ? (
                  <StripePayButton
                    bookingId={info.id}
                    amount={info.amount}
                    onError={(_, code) => { if (code === "payment_expired") loadInfo(); }}
                  />
                ) : (
                  <p className="text-center text-sm text-destructive">
                    Online payment is currently unavailable.
                  </p>
                )}
              </div>
            )}

            {isPaid && (
              <p className="mt-6 text-center text-sm text-[hsl(var(--success))]">
                ✓ {t("pay_already_paid")}
              </p>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Button variant="ghost" className="rounded-full" onClick={() => navigate("/")} data-testid="pay-back-home">
            {t("pay_back_home")}
          </Button>
        </div>
      </main>
    </div>
  );
}
